import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Params, useParams } from 'react-router-dom';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useGetCustomerBoActivity } from '@utils/api/back-office/customer-bo/use-get-customer-bo-activity';
import { useGetModels } from '@utils/api/models/use-get-models';
import { formatDate, formatDuration } from '@utils/date';
import PageHeader from '@components/layout/page-header';
import Loader from '@components/atoms/loaders/loader';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

type Row = {
  id: number | string;
  session_id: string;
  filename: string;
  status: string;
  duration: string;
  language: string;
  date_session_start: string;
  last_update: string;
  is_deleted: boolean;
};

export default function CustomerBoActivity() {
  const { t } = useTranslation();
  const { customerId } = useParams<Params>();
  const cid = Number(customerId) || 0;
  const { getColour } = useThemeContext();

  const { data: supportedLanguages } = useGetModels();
  const { data, isLoading } = useGetCustomerBoActivity(cid);

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];

    return data.result.map(stt => {
      // map language key -> full name when possible
      const langKey = stt.language?.split('-')[0]?.toLowerCase?.();
      const langData = supportedLanguages?.result.find(l => l.key === langKey);
      const fullLangName = langData ? langData.name : stt.language;

      return {
        id: stt.id ?? '',
        session_id: stt.session_id ?? '',
        filename: stt.filename ?? '',
        status: String(stt.status).toLowerCase(),
        duration: formatDuration(stt.duration ?? 0),
        language: fullLangName ?? '',
        date_session_start: formatDate(stt.date_session_start || ''),
        last_update: formatDate(stt.last_update || ''),
        is_deleted: !!stt.is_deleted
      };
    });
  }, [data, supportedLanguages, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('session-id'),
        field: 'session_id',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'session_id',
        serverOperator: 'ilike',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('filename'),
        field: 'filename',
        minWidth: 220,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'filename',
        serverOperator: 'ilike',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('status'),
        field: 'status',
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'status',
        serverOperator: 'eq',
        // Backend accepts string status values: processing, finished, new, uploaded, error, removed, uploading
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('processing'), value: 'processing' },
          { label: t('finished'), value: 'finished' },
          { label: t('error'), value: 'error' },
          { label: t('new'), value: 'new' },
          { label: t('uploaded'), value: 'uploaded' },
          { label: t('removed'), value: 'removed' },
          { label: t('uploading'), value: 'uploading' }
        ],
        cell: ({ row }) => {
          const status = row.original.status;
          return <StatusBadge status={status} label={t(status)} />;
        }
      },
      {
        header: t('duration'),
        field: 'duration',
        maxWidth: 120,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('language'),
        field: 'language',
        maxWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'language',
        serverOperator: 'ilike',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('date-session-start'),
        field: 'date_session_start',
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('last-update'),
        field: 'last_update',
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('is-deleted'),
        field: 'is_deleted',
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'is_deleted',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isDeleted = !!getValue();
          if (!isDeleted) {
            return (
              <span
                className="inline-flex items-center rounded-full border border-hairline bg-app-soft px-2.5 py-0.5 text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('no')}
              </span>
            );
          }
          return <StatusBadge status="deleted" label={t('yes')} />;
        }
      }
    ],
    [t, getColour]
  );

  return (
    <div className="min-w-full">
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className="py-2 px-4 space-y-4">
          <PageHeader
            title={t('customer-activity')}
            caption={t(
              'list-of-all-the-actions-taken-by-the-customer-including-their-status-and-duration'
            )}
          />
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={data?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            height="75vh"
            dense
            grid={false}
            striped
            loading={isLoading || !data}
            getRowId={row => String(row.id)}
          />
        </div>
      )}
    </div>
  );
}
